/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { queryClient } from "$root/third-party-integrations/react-query/typedQueryClient";
import { minutes } from "$root/utils";
import type { MaybePromise } from "@mdotm/utils/promise";
import { type UseQueryResult, useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { Map as ImmutableMap, Set as ImmutableSet } from "immutable";

type NewQueryResult<T, TParams extends any[] = []> = {
	getQueryConfig: (...params: TParams) => UseQueryOptions<T>;
	useQuery: (...params: TParams) => UseQueryResult<T>;
	fetchQuery: (...params: TParams) => Promise<T>;
	/** query key including the repository name */
	fullQueryKey: string[];
	/** query key excluding the repository name */
	partialQueryKey: string[];
};

export type InvalidateEntityProps = {
	skipLists?: boolean;
};

export type InvalidateEntitiesProps = {
	skipLists?: boolean;
};

export function flattenProperties(x: unknown): unknown[] {
	if (x != null && typeof x === "object") {
		if (x instanceof Set || x instanceof ImmutableSet) {
			return Array.from((x as Set<unknown> | ImmutableSet<unknown>).values()).flatMap(flattenProperties);
		}
		if (x instanceof Map || x instanceof ImmutableMap) {
			return Array.from((x as Map<unknown, unknown> | ImmutableMap<unknown, unknown>).values()).flatMap(
				flattenProperties,
			);
		}
		return Object.values(x).flatMap(flattenProperties);
	}
	return [x];
}

export function repositoryHelperFactory(
	repositoryName: string,
	opts?: { listKeys?: unknown[]; repositoryQueryOverrides?: Partial<UseQueryOptions<any>> },
) {
	function invalidate(partialQueryKeys?: readonly unknown[], invalidateOpts?: { async?: false }): void;
	function invalidate(partialQueryKeys: readonly unknown[], invalidateOpts: { async: true }): Promise<void>;
	function invalidate(partialQueryKeys?: readonly unknown[], invalidateOpts?: { async: boolean }): MaybePromise<void>;
	function invalidate(partialQueryKeys?: readonly unknown[], invalidateOpts?: { async?: boolean }) {
		return invalidateQueriesWith(repositoryName, partialQueryKeys, invalidateOpts);
	}

	return {
		newQuery<K extends string, T, TParams extends any[] = []>(
			name: K,
			dataProvider: (...params: TParams) => Promise<T>,
			queryOverrides?: Partial<UseQueryOptions<T>>,
		): NewQueryResult<T, TParams> {
			const queryConfigProvider = (...params: TParams): UseQueryOptions<T> => ({
				queryKey: [repositoryName, name, ...flattenProperties(params)],
				queryFn: () => dataProvider(...params),

				// TODO: use the following parameters (or a similar config) to instantiate the QueryClient
				// cacheTime: minutes(10),
				cacheTime: minutes(5),
				// cacheTime: 0,
				// TODO: for now let's mark everything immediately as stale to avoid out-of-sync data.
				// Once everything goes through repository (and therefore every action performs the appropriate invalidation)
				// we can set the stale time appropriately
				// staleTime: minutes(10),
				staleTime: 0,
				...opts?.repositoryQueryOverrides,
				...queryOverrides,
			});

			return {
				getQueryConfig: (...params: TParams) => queryConfigProvider(...params),
				useQuery: (...params: TParams) => useQuery(queryConfigProvider(...params)),
				fetchQuery: (...params: TParams) => queryClient.fetchQuery(queryConfigProvider(...params)),
				fullQueryKey: [repositoryName, name],
				partialQueryKey: [name],
			} as const;
		},
		newAction<T, TParams extends any[] = []>(
			actionFn: (...params: TParams) => Promise<T>,
		): (...params: TParams) => Promise<T> {
			// TODO: if needed, use this section to implement common action behavior
			return (...params: TParams) => actionFn(...params);
		},
		/**
		 * Invalidates all queries containing the provided UUID and all the queries related to collections that may
		 * include this entity.
		 *
		 * Equivalent to calling the following two functions:
		 * ```ts
		 * invalidateLists();
		 * invalidate(uuid);
		 * ```
		 */
		invalidateEntity(uuid: string, invalidateOpts?: InvalidateEntityProps) {
			if (!invalidateOpts?.skipLists) {
				opts?.listKeys?.forEach((listKey) => {
					invalidateQueriesWith(repositoryName, listKey);
				});
			}
			invalidateQueriesWith(repositoryName, uuid);
		},
		invalidateEntities(uuids: string[], invalidateOpts?: InvalidateEntitiesProps) {
			if (!invalidateOpts?.skipLists) {
				opts?.listKeys?.forEach((listKey) => {
					invalidateQueriesWith(repositoryName, listKey);
				});
			}
			uuids.forEach((uuid) => {
				invalidateQueriesWith(repositoryName, uuid);
			});
		},
		invalidateLists(): void {
			opts?.listKeys?.forEach((listKey) => {
				invalidateQueriesWith(repositoryName, listKey);
			});
		},
		/**
		 * @param partialQueryKeys excluding the repository name (note: they'll be flattened!)
		 */
		invalidate,
	};
}

export function invalidateQueriesWith(base: unknown, extraKeyOrKeys?: unknown, opts?: { async?: false }): void;
export function invalidateQueriesWith(base: unknown, extraKeyOrKeys: unknown, opts: { async: true }): Promise<void>;
export function invalidateQueriesWith(
	base: unknown,
	extraKeyOrKeys?: unknown,
	opts?: { async?: boolean },
): MaybePromise<void>;

/**
 * @param base
 * @param extraKeyOrKeys an array or object (note: it will be flattened to extract primitive keys)
 */
export function invalidateQueriesWith(
	base: unknown,
	extraKeyOrKeys?: unknown,
	opts?: { async?: boolean },
): MaybePromise<void> {
	const extraKeys = extraKeyOrKeys !== undefined ? flattenProperties(extraKeyOrKeys) : [];
	return queryClient
		.invalidateQueries({
			refetchType: "active",
			predicate: (query) =>
				query.queryKey[0] === base && (!extraKeys || extraKeys.some((extra) => query.queryKey.includes(extra))),
		})
		.catch((err) => {
			console.warn("query invalidation failed", err);
			if (opts?.async) {
				throw err;
			}
		});
}
